'use client';

import { useEffect, useLayoutEffect, useState } from 'react';
import { usePathname } from 'next/navigation';

import { cn } from '@utils';

import {
  getOpenMobileGroups,
  headerHeightClass,
  headerScrollThreshold,
  SiteHeaderBrandLink,
  type MobileOpenState,
} from './SiteHeader.shared';
import { SiteHeaderDesktopNavigation } from './SiteHeaderDesktopNavigation';
import { SiteHeaderMobileNavigation } from './SiteHeaderMobileNavigation';
import type { SiteSettingsData } from './types';

type SiteHeaderProps = {
  settings: SiteSettingsData;
};

export function SiteHeader({ settings }: SiteHeaderProps) {
  const pathname = usePathname();

  const navigation = settings.navigation;
  const leftNavigation = navigation.slice(0, 3);
  const rightNavigation = navigation.slice(3);

  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openMobileGroups, setOpenMobileGroups] = useState<MobileOpenState>(() =>
    getOpenMobileGroups(navigation, pathname),
  );

  useLayoutEffect(() => {
    const handleScroll = () => {
      const hasHeroMedia =
        document.querySelector('[data-site-hero-media="true"]') != null;
      const shouldForceSolidDesktop = window.innerWidth >= 1024 && !hasHeroMedia;
      const nextIsScrolled = shouldForceSolidDesktop || window.scrollY > headerScrollThreshold;

      setIsScrolled((current) => (current === nextIsScrolled ? current : nextIsScrolled));
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [pathname]);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setOpenMobileGroups(getOpenMobileGroups(navigation, pathname));
  }, [navigation, pathname]);

  const headerIsSolid = isScrolled || isMobileMenuOpen;

  return (
    <header
      className={cn(
        'fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,box-shadow,backdrop-filter,color] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]',
        headerIsSolid
          ? ' border-primary/10 bg-muted/95 text-primary shadow-[0_16px_40px_-32px_rgba(114,106,100,0.7)] backdrop-blur-md'
          : 'bg-transparent text-white',
      )}
    >
      <div className="container">
        <div className={cn('flex items-center justify-between gap-4 lg:hidden', headerHeightClass)}>
          <SiteHeaderBrandLink
            headerIsSolid={headerIsSolid}
            viewport="mobile"
          />

          <button
            aria-controls="mobile-navigation"
            aria-expanded={isMobileMenuOpen}
            aria-label={isMobileMenuOpen ? 'Sluit menu' : 'Open menu'}
            className={cn(
              'inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full border transition-colors',
              headerIsSolid
                ? 'border-primary/15 bg-white text-primary'
                : 'border-white/30 bg-white/10 text-white backdrop-blur-sm',
            )}
            onClick={() => setIsMobileMenuOpen((current) => !current)}
            type="button"
          >
            <span className="sr-only">Menu</span>
            <svg
              aria-hidden="true"
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
            >
              {isMobileMenuOpen ? (
                <path
                  d="M6 6L18 18M18 6L6 18"
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeWidth="1.8"
                />
              ) : (
                <path
                  d="M4 7H20M4 12H20M4 17H20"
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeWidth="1.8"
                />
              )}
            </svg>
          </button>
        </div>

        <div
          className={cn(
            'hidden lg:grid lg:grid-cols-[1fr_auto_1fr] lg:items-center lg:gap-6',
            headerHeightClass,
          )}
        >
          <SiteHeaderDesktopNavigation
            ariaLabel="Primaire hoofdnavigatie"
            headerIsSolid={headerIsSolid}
            items={leftNavigation}
            pathname={pathname}
          />

          <SiteHeaderBrandLink
            headerIsSolid={headerIsSolid}
            viewport="desktop"
          />

          <SiteHeaderDesktopNavigation
            align="end"
            ariaLabel="Secundaire hoofdnavigatie"
            headerIsSolid={headerIsSolid}
            items={rightNavigation}
            pathname={pathname}
          />
        </div>
      </div>

      <SiteHeaderMobileNavigation
        isOpen={isMobileMenuOpen}
        navigation={navigation}
        onToggleGroup={(label) =>
          setOpenMobileGroups((current) => ({
            ...current,
            [label]: !current[label],
          }))
        }
        openGroups={openMobileGroups}
        pathname={pathname}
      />
    </header>
  );
}
